import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  type Ref,
  useId
} from 'vue'
import { useInjectFormValidator } from './form-validator.composable'
import type { ValidationFn } from '../define-rule'

export type FieldValidatorState = {
  /**
   * Returns the validation error message if the field is invalid.
   */
  error?: string

  /**
   * A field is considered valid if it passes all validation rules.
   */
  isValid: boolean

  /**
   * A field is considered pristine if it has not been modified by the user.
   */
  isPristine?: boolean

  /**
   * A field is considered dirty if it has been modified by the user.
   */
  isDirty?: boolean
}

export type FieldValidatorProps = {
  id?: string
  disabled?: boolean
  rules?: ValidationFn[]
}

function createDefaultState(): FieldValidatorState {
  return {
    error: undefined,
    isValid: false,
    isPristine: true,
    isDirty: false
  }
}

export const useFieldValidator = (value: Ref, props: FieldValidatorProps) => {
  const { register, unregister, updateState: emitState } = useInjectFormValidator()
  const _id = props.id ? props.id : useId()
  const _isDisabled = !!props.disabled
  const _state: Ref<FieldValidatorState> = ref(createDefaultState())
  const _initialValue = ref(value.value)

  const rules = props.rules ?? []
  const isValid = computed(() => _state.value.isValid)
  const error = computed(() => _state.value.error)
  const isPristine = computed(() => _state.value.isPristine)
  const isDirty = computed(() => _state.value.isDirty)
  const state = computed(() => _state.value)

  onBeforeMount(() => {
    register({ id: _id, state: state.value, validate })
  })

  onMounted(async () => {
    await validate(false)
  })

  onBeforeUnmount(() => {
    unregister(_id)
  })

  function resetState(): void {
    const { isValid, error } = createDefaultState()
    updateFieldValidationState(isValid, error)
  }

  async function validate(
    showErrorMessage: boolean = true,
    stateful: boolean = true
  ): Promise<boolean> {
    await nextTick() // wait for DOM updates; otherwise there are constant problems with @update: modelValue events, especially with LSelect

    if (_isDisabled || !rules?.length) {
      resetState()
    }

    const currentValue = value.value
    updateFieldInputState(currentValue)

    for (const rule of rules) {
      try {
        const result = await rule(currentValue)
        if (result === true) continue

        if (stateful) {
          updateFieldValidationState(
            false,
            showErrorMessage ? (typeof result === 'string' ? result : '') : ''
          )
        }

        return false
      } catch (e) {
        console.error(`Error while validation field ${_id}`, e)
      }
    }
    updateFieldValidationState(true, undefined)
    return true
  }

  function updateFieldValidationState(isValid: boolean, error?: string) {
    _state.value.isValid = isValid
    _state.value.error = error
    emitState(_id, _state.value)
  }

  function updateFieldInputState(value: any) {
    if (value !== _initialValue.value) {
      _state.value.isDirty = true

      // only update on first change
      if (_state.value.isPristine === true) _state.value.isPristine = false
    } else {
      _state.value.isDirty = false
    }
  }

  return {
    isValid,
    error,
    isPristine,
    isDirty,
    validate
  }
}
